import { useEffect, useState } from 'react';

export interface SalesforceMCPConfig {
	account: string;
	dataset: string;
	cookieDomain: string;
}

export type SalesforceMCPStatus = 'idle' | 'loading' | 'loaded' | 'error';

export function useSalesforceMCP(config: SalesforceMCPConfig | null): { status: SalesforceMCPStatus } {
	const [status, setStatus] = useState<SalesforceMCPStatus>(() => {
		if (typeof window !== 'undefined' && window.__SALESFORCE_MCP_LOADED__) return 'loaded';
		return 'idle';
	});

	const account = config?.account;
	const dataset = config?.dataset;
	const cookieDomain = config?.cookieDomain;

	useEffect(() => {
		if (!account || !dataset || !cookieDomain) return;

		if (window.__SALESFORCE_MCP_LOADED__) {
			setStatus('loaded');
			return;
		}

		setStatus('loading');

		const script = document.createElement('script');
		script.src = `https://cdn.evgnet.com/beacon/${dataset}/${account}/scripts/evergage.min.js`;

		const handleLoad = () => {
			window.__SALESFORCE_MCP_LOADED__ = true;
			window.__SALESFORCE_MCP_CONFIG__ = { account, dataset, cookieDomain };

			if (window.dataLayer) {
				window.dataLayer.push({ event: 'salesforceMCPBeaconLoaded' });
			}

			setStatus('loaded');
		};

		const handleError = () => {
			setStatus('error');
		};

		script.addEventListener('load', handleLoad);
		script.addEventListener('error', handleError);

		document.head.appendChild(script);

		return () => {
			script.removeEventListener('load', handleLoad);
			script.removeEventListener('error', handleError);
			if (!window.__SALESFORCE_MCP_LOADED__) {
				script.remove();
				setStatus('idle');
			}
		};
	}, [account, dataset, cookieDomain]);

	return { status };
}
